import { Grid } from "@material-ui/core";
import {
  FieldValues,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  Button,
  SelectField,
  SelectFieldOption,
  TextField,
  Title,
} from "@funcional/react-ui";
import {
  formFieldDisplays,
  formFieldEntityFields,
  formFieldTypes,
  formFieldTypesWithOptions,
  IFormField,
  IFormFieldOption,
} from "@pbm/registration-policies/contracts";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormFieldOptionList } from "./FormFieldOptionList";
import { useIntl } from "react-intl";

type FormFieldFormProps = {
  onSubmit: SubmitHandler<FieldValues>;
  onCancel: Function;
  defaults?: IFormField;
  enabledApiExternal?: boolean;
};

const schema = yup.object().shape({
  code: yup.string().required().min(1),
  label: yup.string().required().min(1),
  type: yup.string().required().oneOf(formFieldTypes),
  display: yup.string().required().oneOf(formFieldDisplays),
  entityField: yup
    .string()
    .oneOf([null, ...formFieldEntityFields])
    .nullable(),
});

const containerSpacing = 4;

export const FormFieldForm = ({
  onSubmit,
  onCancel,
  defaults,
  enabledApiExternal,
}: FormFieldFormProps) => {
  const { formatMessage } = useIntl();

  const {
    control,
    handleSubmit,
    watch,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaults,
  });

  const watchType = watch("type", defaults?.type);

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "options",
    keyName: "value",
  });

  const addOption = () => {
    append([{ text: "", value: "" } as never]);
  };
  const editOption = (index: number, value: IFormFieldOption) => {
    update(index, value as never);
  };
  const deleteOption = (row: any) => {
    if (window.confirm("Tem certeza de que deseja excluir este item?")) {
      remove(row.index);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <Grid container spacing={containerSpacing}>
        <Grid item xs={12}>
          <TextField
            required
            name="code"
            label="Código"
            control={control}
            defaultValue={defaults?.code}
            errorMessage={errors?.code?.message}
            readOnly={Boolean(defaults?.code && defaults?.code !== "")}
            helperText="Identificador interno usado somente pelo sistema"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            name="label"
            label="Rótulo"
            control={control}
            defaultValue={defaults?.label}
            errorMessage={errors?.label?.message}
            helperText="Nome que aparece no formulário de inscrição"
          />
        </Grid>
        <Grid item xs={6}>
          <SelectField
            required
            name="type"
            label="Tipo"
            control={control}
            errorMessage={errors?.type?.message}
            defaultValue={defaults?.type || undefined}
          >
            <SelectFieldOption value={undefined}>-</SelectFieldOption>
            {formFieldTypes.map((value) => (
              <SelectFieldOption key={value} value={value}>
                {formatMessage({
                  id: `enums.field_type.${value.toLowerCase()}`,
                })}
              </SelectFieldOption>
            ))}
          </SelectField>
        </Grid>
        <Grid item xs={6}>
          <SelectField
            required
            name="display"
            label="Preenchimento"
            control={control}
            errorMessage={errors?.display?.message}
            defaultValue={defaults?.display || undefined}
          >
            <SelectFieldOption value={undefined}>-</SelectFieldOption>
            {formFieldDisplays.map((value) => (
              <SelectFieldOption key={value} value={value}>
                {formatMessage({
                  id: `enums.field_display.${value.toLowerCase()}`,
                })}
              </SelectFieldOption>
            ))}
          </SelectField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="description"
            label="Descrição para ajuda"
            helperText="Texto de ajuda que aparece embaixo do campo no formulário"
            control={control}
            errorMessage={errors?.description?.message}
            defaultValue={defaults?.description}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="defaultValue"
            label="Valor padrão"
            helperText="Assume este valor se o campo não for preenchido no cadastro"
            control={control}
            errorMessage={errors?.defaultValue?.message}
            defaultValue={defaults?.defaultValue}
          />
        </Grid>

        <Grid item xs={12}>
          <SelectField
            name="entityField"
            label="Entidade"
            helperText="Vincula este campo do formulário a um tipo de dado específico"
            control={control}
            errorMessage={errors?.entityField?.message}
            defaultValue={defaults?.entityField || undefined}
          >
            <SelectFieldOption value={undefined}>-</SelectFieldOption>
            {formFieldEntityFields.map((value) => (
              <SelectFieldOption key={value} value={value}>
                {formatMessage({
                  id: `enums.entity_field.${value.toLowerCase()}`,
                })}
              </SelectFieldOption>
            ))}
          </SelectField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required={enabledApiExternal}
            name="externalApiField"
            label="Api Externa"
            helperText="API externa"
            control={control}
            readOnly={!enabledApiExternal}
            errorMessage={errors?.defaultValue?.message}
            defaultValue={defaults?.externalApiField}
          />
        </Grid>

        {fields &&
        watchType &&
        formFieldTypesWithOptions.includes(watchType) ? (
          <Grid item xs={12}>
            <Title level={3}>Opções válidas</Title>

            <FormFieldOptionList
              register={register as any}
              data={fields}
              addOption={addOption}
              editOption={editOption}
              deleteOption={deleteOption}
            />
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <Button type="submit" color="primary">
            Salvar
          </Button>
          &nbsp;
          <Button onClick={onCancel as any} color="danger">
            Cancelar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
